import clsx from 'clsx';
import type { FormLocale } from '@jlg-website/shared';
import { textFor } from './shared/copy';
import type { CallbackAnswer } from './shared/type';

const QUESTION_ID = 'callback-question';
const GROUP_NAME = 'callback-answer';

type CallbackQuestionProps = {
  /** Only the wording turns on the office day — both answers are offered either way. */
  outsideBusinessHours: boolean;
  /** Null until answered, and again whenever the visitor backs out of the answer they gave. */
  answer: CallbackAnswer | null;
  locale: FormLocale;
  onAnswer: (answer: CallbackAnswer) => void;
};

/**
 * What every call back opens with: be called right away, or pick a time.
 *
 * Outside the office day the same choice is put as a request for permission rather than an offer —
 * we would be calling at an hour the visitor has no reason to expect us.
 *
 * A radio group rather than a pair of buttons, because the answer stays on screen above the
 * calendar it reveals: the visitor can see what they chose, and change it, without leaving the step.
 */
export function CallbackQuestion({
  outsideBusinessHours,
  answer,
  locale,
  onAnswer,
}: CallbackQuestionProps) {
  const question = textFor(locale, outsideBusinessHours ? 'callNowPermission' : 'callNowQuestion');

  return (
    <div className="flex flex-col gap-3">
      <h2 id={QUESTION_ID} className="text-base font-bold text-brand-text-button">
        {question}
      </h2>

      <div role="radiogroup" aria-labelledby={QUESTION_ID} className="grid grid-cols-2 gap-3">
        <AnswerOption
          value="now"
          label={textFor(locale, 'yes')}
          selected={answer === 'now'}
          onSelect={onAnswer}
        />
        <AnswerOption
          value="schedule"
          label={textFor(locale, 'no')}
          selected={answer === 'schedule'}
          onSelect={onAnswer}
        />
      </div>
    </div>
  );
}

type AnswerOptionProps = {
  value: CallbackAnswer;
  label: string;
  selected: boolean;
  onSelect: (answer: CallbackAnswer) => void;
};

/** Both answers carry the same weight — neither is the one we are steering the visitor to. */
function AnswerOption({ value, label, selected, onSelect }: AnswerOptionProps) {
  return (
    <label
      className={clsx(
        'flex cursor-pointer items-center gap-2.5 rounded-lg border px-4 py-3 text-sm transition-colors duration-200',
        selected
          ? 'border-brand-primary bg-brand-primary/5 font-semibold text-brand-text-button'
          : 'border-brand-muted-light bg-white text-brand-text-button'
      )}
    >
      <input
        type="radio"
        name={GROUP_NAME}
        value={value}
        checked={selected}
        onChange={() => onSelect(value)}
        className="size-4 shrink-0 cursor-pointer accent-brand-primary"
      />
      {label}
    </label>
  );
}
